import { useEffect } from 'react';

/**
 * Auto-attaches a subtle 3D mouse-tilt effect to every element
 * carrying `data-tilt` on the page.
 *
 * Lightweight, raf-throttled, respects prefers-reduced-motion.
 */
export const useTilt = ({ maxTilt = 6, scale = 1.01 } = {}) => {
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const nodes = Array.from(document.querySelectorAll('[data-tilt]'));
    const cleanups = [];

    nodes.forEach((node) => {
      let rafId = null;
      let rect = null;

      const updateRect = () => { rect = node.getBoundingClientRect(); };

      const onMove = (event) => {
        if (!rect) updateRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const px = (x / rect.width) - 0.5;
        const py = (y / rect.height) - 0.5;

        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          node.style.transform =
            `perspective(900px) translateY(-6px) rotateX(${(-py * maxTilt).toFixed(2)}deg) rotateY(${(px * maxTilt).toFixed(2)}deg) scale(${scale})`;
          node.style.setProperty('--mx', `${((x / rect.width) * 100).toFixed(1)}%`);
          node.style.setProperty('--my', `${((y / rect.height) * 100).toFixed(1)}%`);
        });
      };

      const onEnter = () => { updateRect(); node.style.transition = 'transform 180ms ease-out'; };
      const onLeave = () => {
        if (rafId) cancelAnimationFrame(rafId);
        node.style.transition = 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1)';
        node.style.transform = '';
      };

      node.addEventListener('mouseenter', onEnter);
      node.addEventListener('mousemove', onMove);
      node.addEventListener('mouseleave', onLeave);
      window.addEventListener('resize', updateRect);

      cleanups.push(() => {
        if (rafId) cancelAnimationFrame(rafId);
        node.removeEventListener('mouseenter', onEnter);
        node.removeEventListener('mousemove', onMove);
        node.removeEventListener('mouseleave', onLeave);
        window.removeEventListener('resize', updateRect);
        node.style.transform = '';
        node.style.transition = '';
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [maxTilt, scale]);
};
